import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import { useCurrentUserContext } from 'contexts/currentUser';
import { useMutation } from 'hooks/graphql/useMutation';
import { useGetAuthorizationApproval } from 'hooks/useGetAuthorizationApprovals';

import type {
  MigrateEthMutation,
  MigrateEthMutationVariables,
  PrepareMigrateEthMutation,
  PrepareMigrateEthMutationVariables,
} from './__generated__/useMigrateEth.graphql';

const PREPARE_MIGRATE_ETH_MUTATION = gql`
  mutation PrepareMigrateEthMutation($input: prepareMigrateEthInput!) {
    prepareMigrateEth(input: $input) {
      weiAmount
      authorization {
        id
        ...useGetAuthorizationApprovals_authorizationRequest
      }
    }
  }
  ${useGetAuthorizationApproval.fragments.authorizationRequest}
` as TypedDocumentNode<
  PrepareMigrateEthMutation,
  PrepareMigrateEthMutationVariables
>;

const MIGRATE_ETH_MUTATION = gql`
  mutation MigrateEthMutation($input: migrateEthInput!) {
    migrateEth(input: $input) {
      currentUser {
        slug
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<MigrateEthMutation, MigrateEthMutationVariables>;

export const useMigrateEth = ({
  forWithdrawal = false,
}: { forWithdrawal?: boolean } = {}) => {
  const { currentUser } = useCurrentUserContext();
  const { shouldMigrateEth, shouldMigrateEthBeforeWithdrawal } =
    currentUser || {};
  const getAuthorizationApproval = useGetAuthorizationApproval();
  const [prepareMigrateEth] = useMutation(PREPARE_MIGRATE_ETH_MUTATION);
  const [migrateEth] = useMutation(MIGRATE_ETH_MUTATION);

  return useCallback(async () => {
    if (
      !shouldMigrateEth ||
      (forWithdrawal && !shouldMigrateEthBeforeWithdrawal)
    )
      return;

    const { data } = await prepareMigrateEth({ variables: { input: {} } });
    if (!data?.prepareMigrateEth) return;

    const { weiAmount, authorization } = data.prepareMigrateEth;
    if (!authorization || !weiAmount) return;

    const authorizationApproval = await getAuthorizationApproval(authorization);
    if (!authorizationApproval) return;

    await migrateEth({
      variables: { input: { approval: authorizationApproval, weiAmount } },
    });
  }, [
    prepareMigrateEth,
    migrateEth,
    getAuthorizationApproval,
    forWithdrawal,
    shouldMigrateEth,
    shouldMigrateEthBeforeWithdrawal,
  ]);
};
